import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { clearContentStudioState } from '../content/contentStudioSnapshot';
import authService from '../services/authService';
import accountStore from '../services/accountStore';
import { connectSocket, disconnectSocket } from '../services/socket';
import { setRuntimeToolAccess, setRuntimeToolsInUse } from '../config/toolAccess';

function applyUserSession(u) {
  if (u?.toolRoleAccess) setRuntimeToolAccess(u.toolRoleAccess);
  if (u?.toolsInUse) setRuntimeToolsInUse(u.toolsInUse);
  return u;
}

const REFRESHABLE_AUTH_CODES = new Set(['TOKEN_EXPIRED', 'INVALID_TOKEN', 'NO_TOKEN']);

/**
 * Restore the session on cold load / hard refresh.
 * Access tokens are short-lived; refresh tokens (localStorage + HttpOnly cookie)
 * are the source of truth when the bearer token is gone or stale.
 */
async function bootstrapSession() {
  const token = accountStore.getAccessToken();
  const active = accountStore.getActiveAccount();

  if (token) {
    try {
      return applyUserSession(await authService.me());
    } catch (err) {
      // Stale bearer — drop it and fall through to refresh.
      if (!REFRESHABLE_AUTH_CODES.has(err?.code)) throw err;
      accountStore.setAccessToken(null);
    }
  }

  if (active?.refreshToken) {
    try {
      return applyUserSession(await authService.refresh(active.refreshToken));
    } catch {
      /* fall through to cookie refresh */
    }
  }

  try {
    return applyUserSession(await authService.refresh());
  } catch {
    return null;
  }
}

const AuthContext = createContext(null);

/**
 * AuthProvider — one active user at a time on this device (account switcher),
 * with concurrent browser tabs sharing that session via localStorage.
 *
 * State exposed to consumers:
 *   user, loading, isAuthenticated              — current session (unchanged)
 *   accounts                                    — every saved account tile
 *   activeAccountId                             — the tile currently active
 *   login(email, password)                      — sign in (adds to accounts)
 *   addAccount(email, password)                 — sign in without dropping current user
 *   switchAccount(userId)                       — silent renew for that account
 *   removeAccount(userId)                       — revoke + drop that account
 *   logout()                                    — sign out current session
 *   logoutAll()                                 — sign out everything, everywhere
 *   refreshUser()                               — re-fetch /me
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState(() => accountStore.listAccounts());

  const syncAccounts = useCallback(() => {
    setAccounts(accountStore.listAccounts());
  }, []);

  useEffect(() => {
    // Cross-app launch handoff: Command Center deep-links here with a one-time
    // ?launchToken=. Adopt it as the session token, then strip it from the URL.
    try {
      const params = new URLSearchParams(window.location.search);
      const launchToken = params.get('launchToken');
      if (launchToken) {
        accountStore.setAccessToken(launchToken);
        params.delete('launchToken');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      }
    } catch (_) { /* ignore */ }

    (async () => {
      try {
        const u = await bootstrapSession();
        setUser(u);
        if (u) syncAccounts();
      } catch (_) {
        // Every silent recovery failed. Drop the access token; the account
        // list stays so the login screen can show tiles.
        accountStore.setAccessToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [syncAccounts]);

  // Connect socket when user is authenticated. Each browser tab owns its own
  // socket instance; the server allows multiple sockets in the same user room.
  useEffect(() => {
    if (user) {
      const token = accountStore.getAccessToken();
      if (token) connectSocket(token);
    } else {
      disconnectSocket();
    }
    return () => disconnectSocket();
  }, [user]);

  // Keep auth state aligned across concurrent tabs (shared localStorage).
  // Logout / account switch / token rotation in one tab must not leave another
  // tab thinking it is still signed in as the previous session.
  useEffect(() => {
    const onStorage = (e) => {
      // Only react to keys written by other tabs (storage events never fire in the writer).
      if (e.key !== 'token' && e.key !== 'seo_taskops_accounts') return;

      if (e.key === 'token' && !e.newValue) {
        disconnectSocket();
        setRuntimeToolAccess(null);
        setUser(null);
        syncAccounts();
        return;
      }

      if (e.key === 'token' && e.newValue) {
        disconnectSocket();
        connectSocket(e.newValue);
        authService.me()
          .then((u) => {
            setUser(applyUserSession(u));
            syncAccounts();
          })
          .catch(() => {
            /* next API call / refresh path will recover or clear */
          });
        return;
      }

      if (e.key === 'seo_taskops_accounts') {
        syncAccounts();
        if (!accountStore.getAccessToken() && !accountStore.getActiveAccount()) {
          disconnectSocket();
          setRuntimeToolAccess(null);
          setUser(null);
        }
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [syncAccounts]);

  const login = useCallback(async (email, password) => {
    const u = applyUserSession(await authService.login(email, password));
    setUser(u);
    syncAccounts();
    return u;
  }, [syncAccounts]);

  /**
   * Like login, but semantically "keep the previous user's tokens on the
   * device". The backend has no notion of an "add" endpoint — it's just a
   * standard login. The magic is that we don't call the store's `clearAll`
   * anywhere in this flow, so the previous account entry stays put.
   */
  const addAccount = useCallback(async (email, password) => {
    const u = applyUserSession(await authService.login(email, password));
    setUser(u);
    syncAccounts();
    return u;
  }, [syncAccounts]);

  const switchAccount = useCallback(async (userId) => {
    const active = accountStore.getActiveAccount();
    // Only skip the network call when we're already signed in as this user.
    // After a hard refresh user can be null while activeUserId still points here.
    if (active && active.id === String(userId) && user) return user;
    const u = applyUserSession(await authService.switchAccount(userId));
    setUser(u);
    syncAccounts();
    return u;
  }, [syncAccounts, user]);

  const removeAccount = useCallback(async (userId) => {
    const res = await authService.removeAccount(userId);
    // If the removed account was active, `res` is the new active user object
    // (or null when there are no saved accounts left).
    if (res && res._id) {
      setUser(applyUserSession(res));
    } else if (res === null) {
      // Active account was removed and there was nothing to fall back to.
      const stillActive = accountStore.getActiveAccount();
      if (!stillActive) setUser(null);
    }
    syncAccounts();
  }, [syncAccounts]);

  const logout = useCallback(async () => {
    // Forget this user's Content Studio state before the session goes. Left behind,
    // the next person to sign in on this browser inherits their working location.
    clearContentStudioState(user?._id ? String(user._id) : '');
    const next = await authService.logout();
    if (next && next._id) {
      // Another saved account was silently activated.
      setUser(applyUserSession(next));
    } else {
      disconnectSocket();
      setRuntimeToolAccess(null);
      setUser(null);
    }
    syncAccounts();
  }, [syncAccounts, user]);

  const logoutAll = useCallback(async () => {
    clearContentStudioState(user?._id ? String(user._id) : '');
    await authService.logoutAll();
    disconnectSocket();
    setRuntimeToolAccess(null);
    setUser(null);
    syncAccounts();
  }, [syncAccounts, user]);

  const refreshUser = useCallback(async () => {
    const u = applyUserSession(await authService.me());
    setUser(u);
    return u;
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated: !!user,
    accounts,
    activeAccountId: user?._id ? String(user._id) : (accountStore.getActiveAccount()?.id || null),
    login,
    addAccount,
    switchAccount,
    removeAccount,
    logout,
    logoutAll,
    refreshUser,
    setUser,
  }), [user, loading, accounts, login, addAccount, switchAccount, removeAccount, logout, logoutAll, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
